import {Box, SvgIcon, Typography, ButtonBase} from '@mui/material';
import ManageIndicesDetailGrid from '../data-grid/ManageIndicesDetailGrid';
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useNavigate} from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useTranslation} from 'react-i18next';
import './ManageIndicesDetail.scss';
import ManageIndicesHeader from '../header/ManageIndicesHeader';
import {ReactComponent as DownloadIcon} from '../../../../../assets/images/download_icon.svg';
import EditIndex from '../add-edit-index/edit-index/EditIndex';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {
  MANAGE_INDEX_DATA_UPDATED_NOTIFICATION,
  ToastType,
} from '../../../../../shared/constants';
import {EditIndexDetails} from '../../helpers/aihelper';
import {getCsvDownloadForIndexDetail} from '../../../../../api/endpoints/aiInvestigateApi';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  IAIInvestigateIndicesPayload,
  ManageIndexDataUpdatedNotification,
} from '../../../../../api/types/aiInvestigate.type';
import {useGetIndicesList} from '../../../../../hooks/ai/investigate/useInvestigate';

const INPROCESS_STATUS = ['in progress', 'inqueue', 'inretryqueue'];

const ManageIndicesDetail: FC = () => {
  const [openEditPanel, setOpenEditPanel] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType?.Success);
  const [editDetails, setEditDetails] = useState<EditIndexDetails | null>(null);
  const [gridRefetchTrigger, setGridRefetchTrigger] = useState(false);
  const [isEditIndexEnabled, setIsEditIndexEnabled] = useState(false);
  const [documentCount, setDocumentCount] = useState<number>(0);
  const navigate = useNavigate();
  const {t} = useTranslation();
  const location = useLocation();
  const row = location.state;
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const [currentIndexName, setCurrentIndexName] = useState<string>(
    row?.indexName ?? ''
  );

  // Fetch indices data to get the latest status of the index and enable/disable edit button accordingly
  const indicesPayload: IAIInvestigateIndicesPayload = {
    sortingColumn: 'LastModifiedDate',
    direction: 'DESC',
    columnFilter: [
      {columnName: 'IndexId', value: row?.indexId?.toString() ?? ''},
    ],
  };
  const {data: indicesData, refetch: refetchIndicesData} =
    useGetIndicesList(indicesPayload);

  const isActiveIndex = row?.active ?? false;

  const handleBackNavigation = () => {
    navigate('/AI/Investigate/manage-indices');
  };
  type HeaderButtonDetail = {
    title?: string;
    onClick: () => void;
    icon?: React.ReactNode;
    iconOnly?: boolean;
    ariaLabel?: string;
    disabled?: boolean;
    tooltip?: string;
    dataTestId: string;
  };
  const handleDownload = useCallback(async () => {
    try {
      const indexId = row?.indexId;

      if (!indexId) {
        console.error('indexId is missing');
        return;
      }

      const response = await getCsvDownloadForIndexDetail(indexId);
      const {blob, filename} = response;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document.csv';

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed', error);
      setToastMessage(t('ai.investigate.manageIndicesDetail.downloadError'));
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  }, [row]);

  const handleEditIndex = () => {
    setOpenEditPanel(true);
  };
  const closePanel = useCallback(() => setOpenEditPanel(false), []);

  const onIndexEdited = useCallback((updatedIndexName?: string) => {
    if (updatedIndexName) {
      setCurrentIndexName(updatedIndexName);
    }
    refetchIndicesData();
    setGridRefetchTrigger((prev) => !prev);
  }, []);

  const buttonDetails: HeaderButtonDetail[] = useMemo(
    () => [
      {
        onClick: handleDownload,
        iconOnly: true,
        disabled: false,
        ariaLabel: 'Download indices details',
        tooltip: `${t('ai.investigate.manageIndicesDetail.download')}`,
        dataTestId: 'Download',

        icon: (
          <SvgIcon
            component={DownloadIcon}
            inheritViewBox
            aria-hidden="true"
            focusable="false"
          />
        ),
      },
      {
        title: `${t('ai.investigate.chatPanel.editIndex')}`,
        onClick: handleEditIndex,
        dataTestId: 'edit',
        disabled: !isActiveIndex || !isEditIndexEnabled,
      },
    ],
    [t, handleEditIndex, handleDownload, isActiveIndex, isEditIndexEnabled]
  );
  const indexName = currentIndexName;
  const indexId = row?.indexId;
  if (!indexId) {
    console.error('indexId is missing');
    return;
  }

  useEffect(() => {
    if (indexName) {
      setEditDetails({indexName, indexId});
    }
  }, [indexId, indexName]);

  // Listen to SignalR events for index data updates and enable/disable edit button based on index status
  useEffect(() => {
    if (lastEvent?.payload?.type !== MANAGE_INDEX_DATA_UPDATED_NOTIFICATION)
      return;
    const notification: ManageIndexDataUpdatedNotification =
      lastEvent!.payload.payload;

    if (Number(row.indexId) !== notification.indexId) return;
    setIsEditIndexEnabled(
      !INPROCESS_STATUS.includes(notification.indexStatus.toLowerCase())
    );
  }, [lastEvent]);

  useEffect(() => {
    if (indicesData?.indices.length) {
      setIsEditIndexEnabled(
        !INPROCESS_STATUS.includes(
          indicesData.indices[0].indexStatus.toLowerCase()
        )
      );
    }
  }, [indicesData]);

  const toastHandler = useCallback(
    (message: string, status: ToastType): void => {
      setToastMessage(message);
      setToastStatus(status);
      setShowToast(true);
    },
    [setToastMessage, setToastStatus, setShowToast]
  );

  return (
    <Box
      className="manage-indices-detail-container"
      component="div"
      role="region"
      aria-labelledby="manage-indices-detail-title"
      aria-describedby="manage-indices-detail-description"
    >
      <Typography
        id="manage-indices-detail-description"
        component="p"
        className="sr-only"
        data-testid="manage-indices-detail-header-tooltip"
      >
        {t('ai.investigate.manageIndicesDetail.headerTooltip')}
      </Typography>
      <Box>
        <Box className="manage-indices-header">
          <ManageIndicesHeader
            title={indexName}
            titleId="manage-indices-detail-title"
            tooltipContent={t(
              'ai.investigate.manageIndicesDetail.indicesTitleInfo'
            )}
            buttonDetails={buttonDetails}
          />
        </Box>
        <Box
          className="header-container"
          component="nav"
          aria-label={t('breadcrumb', 'Breadcrumb')}
        >
          <ButtonBase
            onClick={handleBackNavigation}
            aria-label={t(
              'ai.investigate.manageIndicesDetail.breadcrumbLinkAria'
            )}
            className="breadcrumb-content"
            data-testid={'handle-back'}
          >
            <Box className="icon-box" aria-hidden="true">
              <ArrowBackIcon
                aria-hidden="true"
                focusable="false"
                className="button-arrow"
              />
            </Box>
            <Typography
              component="span"
              className="breadcrumb-text"
              data-testid="manage-indices-detail-header-title"
            >
              {t('ai.investigate.manageIndicesDetail.headerTitle')}
            </Typography>
          </ButtonBase>
          <Box className="header-right-container">
            <Typography
              className="header-right-title "
              data-testid="indices-detail-doc-count-label"
            >
              {t('ai.investigate.manageIndicesDetail.documentsInIndex')}
            </Typography>
            <Typography
              className="header-right"
              data-testid="indices-detail-doc-count"
            >
              {documentCount.toLocaleString()}
            </Typography>
          </Box>
        </Box>
        {!isActiveIndex && (
          <Box
            className="indices-detail-error-container"
            data-testid="indices-detail-error-container"
          >
            <Box
              className="indices-detail-error-banner"
              data-testid="indices-detail-error-banner"
              role="status"
              aria-live="polite"
            >
              <Box
                className="indices-detail-error-icon"
                data-testid="indices-detail-error-icon"
              >
                <InfoIcon data-testid="manage-indices-detail-error-svg" />
              </Box>
              <Typography
                className="indices-detail-error-text typo-text body3_size"
                data-testid="indices-detail-error-banner-message"
              >
                {t(
                  'ai.investigate.manageIndicesDetail.inactiveIndexErrorBanner'
                )}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        className="manage-indices-body"
        component="section"
        aria-label={t('ai.investigate.manageIndicesDetail.gridSection')}
      >
        <ManageIndicesDetailGrid
          indexId={indexId}
          hasAnyIndexPending={(hasPending) =>
            setIsEditIndexEnabled(!hasPending)
          }
          triggerRefetch={gridRefetchTrigger}
          documentCountChange={setDocumentCount}
        />
      </Box>
      {openEditPanel && (
        <EditIndex
          openPanel={openEditPanel}
          onClose={closePanel}
          onEdited={onIndexEdited}
          showToast={toastHandler}
          editDetails={editDetails}
        />
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
};

export default ManageIndicesDetail;
